import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'wouter';
import type { SearchRecord } from '@/types/wiki';
import { Search, X, ShieldAlert, ArrowRight, CornerDownLeft, MapPin, Users, Skull, Box, Layers, Scroll, BookOpen, Compass } from 'lucide-react';

interface SearchModalProps {
  isOpen: boolean;
  onClose: () => void;
}

let cachedIndex: SearchRecord[] | null = null;

export const SearchModal: React.FC<SearchModalProps> = ({ isOpen, onClose }) => {
  const [, setLocation] = useLocation();
  const [query, setQuery] = useState('');
  const [indexData, setIndexData] = useState<SearchRecord[]>(cachedIndex || []);
  const [results, setResults] = useState<SearchRecord[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // Lazy load search index when modal is opened
  useEffect(() => {
    if (isOpen && (!cachedIndex || cachedIndex.length === 0)) {
      import('@/data/generated/search-index.json').then((mod) => {
        const data = mod.default as SearchRecord[];
        cachedIndex = data;
        setIndexData(data);
        setResults(data.slice(0, 8));
      });
    }
  }, [isOpen]);

  // Focus input on open
  useEffect(() => {
    if (isOpen) {
      setQuery('');
      if (indexData.length > 0) setResults(indexData.slice(0, 8));
      setSelectedIndex(0);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [isOpen, indexData]);

  // Search query filter
  useEffect(() => {
    const q = query.trim().toLowerCase();
    if (!q) {
      setResults(indexData.slice(0, 8));
      setSelectedIndex(0);
      return;
    }

    const filtered = indexData
      .map((item) => {
        let score = 0;
        const titleLower = (item.title || '').toLowerCase();
        const subLower = (item.subtitle || '').toLowerCase();
        const snippetLower = (item.snippet || '').toLowerCase();

        if (titleLower === q) score += 100;
        else if (titleLower.startsWith(q)) score += 60;
        else if (titleLower.includes(q)) score += 40;

        if (subLower.includes(q)) score += 20;
        if (item.tags?.some((t) => t && typeof t === 'string' && t.toLowerCase().includes(q))) score += 15;
        if (snippetLower.includes(q)) score += 5;

        return { item, score };
      })
      .filter((res) => res.score > 0)
      .sort((a, b) => b.score - a.score)
      .map((res) => res.item)
      .slice(0, 20);

    setResults(filtered);
    setSelectedIndex(0);
  }, [query]);

  // Keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex((prev) => (prev < results.length - 1 ? prev + 1 : 0));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex((prev) => (prev > 0 ? prev - 1 : results.length - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (results[selectedIndex]) {
        handleSelect(results[selectedIndex]);
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
    }
  };

  const handleSelect = (record: SearchRecord) => {
    onClose();
    setLocation(record.url);
  };

  if (!isOpen) return null;

  const getTypeIcon = (type: SearchRecord['type']) => {
    switch (type) {
      case 'region': return <Compass className="w-4 h-4 text-amber-400" />;
      case 'location': return <MapPin className="w-4 h-4 text-emerald-400" />;
      case 'character': return <Users className="w-4 h-4 text-sky-400" />;
      case 'dungeon': return <Skull className="w-4 h-4 text-rose-400" />;
      case 'enemy': return <Skull className="w-4 h-4 text-rose-500" />;
      case 'item': return <Box className="w-4 h-4 text-purple-400" />;
      case 'set': return <Layers className="w-4 h-4 text-amber-400" />;
      case 'quest': return <Scroll className="w-4 h-4 text-amber-300" />;
      case 'story': return <BookOpen className="w-4 h-4 text-cyan-400" />;
      default: return <Search className="w-4 h-4 text-gray-400" />;
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Wiki-Suche"
      className="fixed inset-0 z-50 flex items-start justify-center pt-16 sm:pt-28 px-4 bg-black/85 backdrop-blur-md animate-in fade-in duration-150"
      onClick={onClose}
    >
      <div
        className="w-full max-w-2xl bg-[#0c0e15]/95 border border-white/10 rounded-2xl shadow-[0_0_60px_rgba(0,0,0,0.85)] overflow-hidden flex flex-col max-h-[80vh] backdrop-blur-2xl"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={handleKeyDown}
      >
        {/* Search Input Bar */}
        <div className="flex items-center px-5 py-4 border-b border-white/[0.08] bg-[#12151f]">
          <Search className="w-5 h-5 text-amber-400 mr-3.5 shrink-0" />
          <input
            ref={inputRef}
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Suche nach Regionen, Bossen, Items, Quests, NPCs..."
            className="w-full bg-transparent text-white placeholder-[#726c62] text-base focus:outline-none"
            aria-autocomplete="list"
          />
          {query && (
            <button
              onClick={() => setQuery('')}
              className="p-1 text-[#8a857b] hover:text-white mr-2 cursor-pointer transition-colors"
              title="Eingabe löschen"
            >
              <X className="w-4 h-4" />
            </button>
          )}
          <kbd className="px-2 py-1 text-[11px] font-mono text-[#8a857b] bg-[#1a1f2c] border border-white/10 rounded-lg">
            ESC
          </kbd>
        </div>

        {/* Results List */}
        <div ref={listRef} className="overflow-y-auto p-3 space-y-1.5 scrollbar-thin">
          {results.length === 0 ? (
            <div className="py-16 text-center text-[#8a857b] space-y-2">
              <Search className="w-10 h-10 mx-auto opacity-30" />
              <p className="text-base text-white font-medium">Keine Treffer für „{query}“</p>
              <p className="text-xs text-[#726c62]">Prüfe die Schreibweise oder suche nach einer Region oder Kategorie.</p>
            </div>
          ) : (
            results.map((record, index) => {
              const isSelected = index === selectedIndex;
              return (
                <div
                  key={`${record.type}-${record.id}`}
                  role="option"
                  aria-selected={isSelected}
                  onMouseEnter={() => setSelectedIndex(index)}
                  onClick={() => handleSelect(record)}
                  className={`flex items-start gap-3.5 p-3.5 rounded-xl cursor-pointer transition-all ${
                    isSelected 
                      ? 'bg-[#181d2a] border border-amber-400/40 text-white shadow-lg' 
                      : 'hover:bg-[#12151e] border border-transparent text-[#c5beb3]'
                  }`}
                >
                  <div className={`mt-0.5 p-2 rounded-xl shrink-0 ${
                    isSelected ? 'bg-black/60 border border-amber-400/30' : 'bg-[#141722] border border-white/[0.04]'
                  }`}>
                    {getTypeIcon(record.type)}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className="text-[10px] uppercase font-mono font-bold tracking-wider text-amber-400">
                        {record.typeLabel}
                      </span>
                      {record.spoilerLevel !== 'spoiler-free' && (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-mono bg-amber-950/80 text-amber-300 border border-amber-800/40">
                          <ShieldAlert className="w-2.5 h-2.5" /> Spoiler
                        </span>
                      )}
                    </div>
                    <h4 className="text-sm font-heading font-bold text-white truncate">
                      {record.title}
                    </h4>
                    {record.subtitle && (
                      <p className="text-xs text-[#8a857b] truncate mt-0.5">
                        {record.subtitle}
                      </p>
                    )}
                    {record.snippet && (
                      <p className="text-xs text-[#726c62] line-clamp-1 mt-1 leading-relaxed">
                        {record.snippet}
                      </p>
                    )}
                  </div>

                  <div className="shrink-0 self-center">
                    {isSelected ? (
                      <CornerDownLeft className="w-4 h-4 text-amber-400" />
                    ) : (
                      <ArrowRight className="w-4 h-4 text-[#4a4f5c]" />
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Footer shortcuts */}
        <div className="px-5 py-3 bg-[#0a0c10] border-t border-white/[0.06] flex items-center justify-between text-xs text-[#726c62]">
          <div className="flex items-center gap-3">
            <span><kbd className="px-1.5 py-0.5 bg-[#141722] rounded text-[10px] font-mono border border-white/10">↑</kbd> <kbd className="px-1.5 py-0.5 bg-[#141722] rounded text-[10px] font-mono border border-white/10">↓</kbd> Navigieren</span>
            <span><kbd className="px-1.5 py-0.5 bg-[#141722] rounded text-[10px] font-mono border border-white/10">↵</kbd> Auswählen</span>
          </div>
          <span className="font-mono">{indexData.length || '1390+'} Einträge im Index</span>
        </div>
      </div>
    </div>
  );
};
